/**
 * Keyboard shortcuts — one list, read by the key handler and by the help
 * dialog, so the dialog cannot describe a shortcut that does not exist.
 *
 * Browsers keep ⌘1–⌘9, ⌘W and ⌘, for themselves, so in a tab the pages are
 * reached with two-key sequences instead (G then F for Flights), which no
 * browser claims. The desktop app has a real menu bar, and those shortcuts
 * are its menu accelerators (`src-tauri/src/lib.rs`); they are listed here
 * only for the dialog.
 */

export type ShortcutScope = 'everywhere' | 'desktop' | 'logbook';

export interface Shortcut {
  /** Key tokens as shown. `Mod` becomes ⌘ or Ctrl. */
  keys: readonly string[];
  /** Pressed one after the other rather than together. */
  sequence?: boolean;
  label: string;
  scope: ShortcutScope;
}

export const SHORTCUTS: readonly Shortcut[] = [
  { keys: ['Mod', 'O'], label: 'Import', scope: 'everywhere' },
  { keys: ['G', 'F'], sequence: true, label: 'Flights', scope: 'everywhere' },
  { keys: ['G', 'L'], sequence: true, label: 'Logbook', scope: 'everywhere' },
  { keys: ['G', 'A'], sequence: true, label: 'Aircraft', scope: 'everywhere' },
  { keys: ['G', 'S'], sequence: true, label: 'Settings', scope: 'everywhere' },
  { keys: ['?'], label: 'Keyboard shortcuts', scope: 'everywhere' },
  { keys: ['Mod', '1'], label: 'Flights', scope: 'desktop' },
  { keys: ['Mod', '2'], label: 'Logbook', scope: 'desktop' },
  { keys: ['Mod', '3'], label: 'Aircraft', scope: 'desktop' },
  { keys: ['Mod', ','], label: 'Settings', scope: 'desktop' },
  { keys: ['Mod', 'Shift', 'S'], label: 'Sync now', scope: 'desktop' },
  { keys: ['Mod', 'Shift', 'B'], label: 'Back up', scope: 'desktop' },
  { keys: ['Mod', 'P'], label: 'Print logbook', scope: 'desktop' },
  { keys: ['↑', '↓', '←', '→'], label: 'Move between cells', scope: 'logbook' },
  { keys: ['Enter'], label: 'Edit cell', scope: 'logbook' },
  { keys: ['Esc'], label: 'Cancel edit', scope: 'logbook' },
  { keys: ['Delete'], label: 'Clear cell', scope: 'logbook' },
  { keys: ['Mod', 'D'], label: 'Fill down', scope: 'logbook' },
  { keys: ['Mod', 'Z'], label: 'Undo', scope: 'logbook' },
];

export function keyLabel(token: string, apple: boolean): string {
  if (token === 'Mod') return apple ? '⌘' : 'Ctrl';
  if (token === 'Shift') return apple ? '⇧' : 'Shift';
  return token;
}

/** Where to go for the second key of a `G` sequence. */
export const GO_TO: Readonly<Record<string, string>> = {
  f: '/flights',
  l: '/logbook',
  a: '/aircraft',
  s: '/settings',
};

/** Typing into a field is never a shortcut. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

export type ShortcutAction =
  { kind: 'navigate'; to: string } | { kind: 'import' } | { kind: 'help' } | null;

/**
 * What a keydown means, given whether the previous key was a lone `G`.
 *
 * @param desktop  In the desktop app ⌘O belongs to the File menu, which
 *   opens the native picker; handling it here as well would open two.
 */
export function interpretKey(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>,
  pendingG: boolean,
  desktop: boolean,
): ShortcutAction {
  const mod = event.metaKey || event.ctrlKey;
  if (mod && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'o') {
    return desktop ? null : { kind: 'import' };
  }
  if (mod || event.altKey) {
    return null;
  }
  if (event.key === '?') {
    return { kind: 'help' };
  }
  if (pendingG) {
    const to = GO_TO[event.key.toLowerCase()];
    return to === undefined ? null : { kind: 'navigate', to };
  }
  return null;
}
